/**
 * Reports API Service
 */

import { apiService as api } from '../../../shared/services/api';
import { GeneratedReport, ReportGenerationRequest, ReportGenerationResponse } from '../types';

export interface ExportedReportFile {
  file: File;
  filename: string;
}

export class ReportsService {
  private getExpectedMimeType(format: string): string {
    return format.toLowerCase() === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8';
  }

  private getFallbackFilename(reportId: number, format: string): string {
    return `report_${reportId}.${format.toLowerCase()}`;
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) {
      return fallback;
    }

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return quotedMatch[1];
    }

    const unquotedMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (unquotedMatch?.[1]) {
      return unquotedMatch[1].trim();
    }

    return fallback;
  }

  /**
   * Get generated reports history
   */
  async getGeneratedReports(museum?: string): Promise<GeneratedReport[]> {
    const url = museum ? `/reports/history/?museum=${museum}` : '/reports/history/';
    const response = (await api.get(url)) as any;
    // Handle paginated response - return the results array
    return response.results || response;
  }

  /**
   * Generate a new report
   */
  async generateReport(request: ReportGenerationRequest): Promise<ReportGenerationResponse> {
    return api.post('/reports/generate/', request);
  }

  /**
   * Delete a generated report and its stored file
   */
  async deleteReport(reportId: number): Promise<void> {
    await api.delete(`/reports/history/${reportId}/`);
  }

  /**
   * Download/export a generated report
   */
  async exportReport(reportId: number, format: string): Promise<ExportedReportFile> {
    console.log('ReportsService: Starting export for report ID:', reportId, 'format:', format);

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const baseURL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api';
    const url = `${baseURL}/reports/export/${reportId}/${format}/`;

    console.log('ReportsService: Making request to:', url);
    console.log('ReportsService: Using token:', token.substring(0, 20) + '...');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('ReportsService: Response status:', response.status);
    console.log('ReportsService: Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ReportsService: Error response:', errorText);
      throw new Error(`Failed to download report: ${response.status} ${response.statusText}`);
    }

    const rawBlob = await response.blob();
    const normalizedBlob =
      rawBlob.type === this.getExpectedMimeType(format)
        ? rawBlob
        : new Blob([await rawBlob.arrayBuffer()], { type: this.getExpectedMimeType(format) });
    const fallbackFilename = this.getFallbackFilename(reportId, format);
    const filename = this.getFilenameFromContentDisposition(
      response.headers.get('content-disposition'),
      fallbackFilename,
    );
    const file = new File([normalizedBlob], filename, {
      type: this.getExpectedMimeType(format),
    });

    console.log('ReportsService: Downloaded blob size:', file.size, 'type:', file.type, 'filename:', filename);

    return { file, filename };
  }

  /**
   * Get recent activities from NHB API
   */
  async getRecentActivities(limit: number = 10): Promise<{
    activities: Array<{
      id: number;
      type: string;
      message: string;
      timestamp: string;
      status: string;
      raw_timestamp: string;
    }>;
    total: number;
    success: boolean;
  }> {
    return api.get(`/reports/recent-activities/?limit=${limit}`);
  }
}

export const reportsService = new ReportsService();
